import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  Position,
  Handle,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Loader2,
  Database,
  ArrowLeft,
  RefreshCw,
  Maximize2,
  Download,
  Layers,
  Eye,
  EyeOff,
  RotateCcw,
  Shuffle,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

// Custom node component for tables
const TableNode = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isDimension = data.name.startsWith("DIM_");
  const isFact = data.name.startsWith("FACT_");

  const nodeColor = isDimension
    ? "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700"
    : isFact
    ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700"
    : "bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-600";

  const headerColor = isDimension
    ? "bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200"
    : isFact
    ? "bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200"
    : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200";

  return (
    <div
      className={`border-2 rounded-lg shadow-lg min-w-64 ${nodeColor} relative`}
    >
      {/* Handles pour les connexions */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "#6366f1", border: "2px solid #6366f1" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "#6366f1", border: "2px solid #6366f1" }}
      />

      {/* Table Header */}
      <div
        className={`px-3 py-2 rounded-t-md ${headerColor} flex items-center justify-between`}
      >
        <div className="flex items-center space-x-2">
          <Database className="h-4 w-4" />
          <span className="font-medium text-sm">{data.name}</span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-white/20 rounded"
        >
          {isExpanded ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </button>
      </div>

      {/* Columns */}
      <div className="px-3 py-2">
        {/* Always show primary key */}
        {data.columns
          .filter((col) => col.primary_key)
          .map((column) => (
            <div
              key={column.name}
              className="flex items-center justify-between py-1 text-xs"
            >
              <div className="flex items-center space-x-1">
                <span className="font-mono font-medium dark:text-white">
                  {column.name}
                </span>
                <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 text-xs px-1 py-0">
                  PK
                </Badge>
              </div>
              <span className="text-gray-500 text-xs">{column.type}</span>
            </div>
          ))}

        {/* Show foreign keys */}
        {data.columns
          .filter((col) => col.foreign_key)
          .map((column) => (
            <div
              key={column.name}
              className="flex items-center justify-between py-1 text-xs"
            >
              <div className="flex items-center space-x-1">
                <span className="font-mono dark:text-white">{column.name}</span>
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs px-1 py-0">
                  FK
                </Badge>
              </div>
              <span className="text-gray-500 text-xs">{column.type}</span>
            </div>
          ))}

        {/* Show other columns if expanded */}
        {isExpanded &&
          data.columns
            .filter(
              (col) => !col.primary_key && !col.foreign_key && !col.system
            )
            .slice(0, 5) // Limit to avoid too tall nodes
            .map((column) => (
              <div
                key={column.name}
                className="flex items-center justify-between py-1 text-xs"
              >
                <span className="font-mono text-gray-700 dark:text-gray-300">
                  {column.name}
                </span>
                <span className="text-gray-500 text-xs">{column.type}</span>
              </div>
            ))}

        {/* Show count if there are more columns */}
        {!isExpanded &&
          data.columns.filter((col) => !col.primary_key && !col.foreign_key)
            .length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              +
              {
                data.columns.filter(
                  (col) => !col.primary_key && !col.foreign_key && !col.system
                ).length
              }{" "}
              more columns
            </div>
          )}
      </div>
    </div>
  );
};

const nodeTypes = {
  table: TableNode,
};

const DWHVisualization = () => {
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDimensions, setShowDimensions] = useState(true);
  const [showFacts, setShowFacts] = useState(true);
  const [showOthers, setShowOthers] = useState(true);
  const [layoutType, setLayoutType] = useState("hierarchical"); // hierarchical, circular, force

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Fetch schemas
  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth("/dwh/schemas");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const schemasArray = Object.values(data.schemas);

      // Fetch details for each schema
      const schemasWithDetails = await Promise.all(
        schemasArray.map(async (schema) => {
          try {
            const detailResponse = await fetchWithAuth(
              `/dwh/schemas/${schema.name}`
            );
            if (detailResponse.ok) {
              const detailData = await detailResponse.json();
              return {
                ...schema,
                properties: detailData.schema.properties,
                column_order: detailData.column_order,
              };
            }
            return schema;
          } catch (err) {
            console.error(`Failed to fetch details for ${schema.name}:`, err);
            return schema;
          }
        })
      );

      setSchemas(schemasWithDetails);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Convert schemas to nodes and edges
  const { filteredNodes, filteredEdges } = useMemo(() => {
    if (!schemas.length) return { filteredNodes: [], filteredEdges: [] };

    console.log("Schemas loaded:", schemas.length);

    // First, create ALL nodes regardless of filters (for edge targets)
    const allNodes = [];
    schemas.forEach((schema, index) => {
      // Convert properties to columns
      const columns = schema.properties
        ? (schema.column_order || Object.keys(schema.properties)).map(
            (name) => {
              const config = schema.properties[name];
              return {
                name,
                type: Array.isArray(config.type)
                  ? config.type.join(" | ")
                  : config.type,
                primary_key: config.primary_key || false,
                foreign_key: config.foreign_key || null,
                system: config.system || false,
              };
            }
          )
        : [];

      // Advanced layout algorithms inspired by SQL Habit visualizer
      const isDimension = schema.name.startsWith("DIM_");
      const isFact = schema.name.startsWith("FACT_");

      // Get all relations for this schema to understand connectivity
      const outgoingFKs = columns
        .filter((col) => col.foreign_key)
        .map((col) => col.foreign_key.table);
      const incomingFKs = schemas
        .filter((s) => s.properties)
        .reduce((acc, s) => {
          const cols = (s.column_order || Object.keys(s.properties)).filter(
            (name) => {
              const config = s.properties[name];
              return config.foreign_key?.table === schema.name;
            }
          );
          if (cols.length > 0) acc.push(s.name);
          return acc;
        }, []);

      let x, y;

      if (layoutType === "hierarchical") {
        // Hierarchical layout: Facts at center, Dimensions around
        if (isDimension) {
          const dimSchemas = schemas.filter((s) => s.name.startsWith("DIM_"));
          const dimIndex = dimSchemas.indexOf(schema);
          const totalDims = dimSchemas.length;

          // Arrange dimensions in a circle around the center
          const radius = 600;
          const angle = (dimIndex / totalDims) * 2 * Math.PI;

          x = 800 + radius * Math.cos(angle);
          y = 400 + radius * Math.sin(angle);

          // Highly connected dimensions closer to center
          if (incomingFKs.length > 2) {
            x = 800 + radius * 0.7 * Math.cos(angle);
            y = 400 + radius * 0.7 * Math.sin(angle);
          }
        } else if (isFact) {
          // Facts in the center with spacing
          const factSchemas = schemas.filter((s) => s.name.startsWith("FACT_"));
          const factIndex = factSchemas.indexOf(schema);

          x = 800 + (factIndex - factSchemas.length / 2) * 300;
          y = 400 + (factIndex % 2) * 200 - 100;
        } else {
          // Other tables at bottom
          const otherSchemas = schemas.filter(
            (s) => !s.name.startsWith("DIM_") && !s.name.startsWith("FACT_")
          );
          const otherIndex = otherSchemas.indexOf(schema);

          x = 400 + otherIndex * 400;
          y = 1000;
        }
      } else if (layoutType === "circular") {
        // Circular layout: all tables in a big circle
        const allSchemas = schemas;
        const schemaIndex = allSchemas.indexOf(schema);
        const totalSchemas = allSchemas.length;

        const radius = Math.max(400, totalSchemas * 30);
        const angle = (schemaIndex / totalSchemas) * 2 * Math.PI;

        x = 800 + radius * Math.cos(angle);
        y = 400 + radius * Math.sin(angle);
      } else if (layoutType === "force") {
        // Force-directed layout simulation (simplified)
        const dimSchemas = schemas.filter((s) => s.name.startsWith("DIM_"));
        const factSchemas = schemas.filter((s) => s.name.startsWith("FACT_"));

        if (isDimension) {
          const dimIndex = dimSchemas.indexOf(schema);
          // Spread dimensions on left side with more space
          x = 200 + (dimIndex % 3) * 500;
          y = 100 + Math.floor(dimIndex / 3) * 350;

          // Push highly connected tables towards center
          if (incomingFKs.length > 1) {
            x += 200;
          }
        } else if (isFact) {
          const factIndex = factSchemas.indexOf(schema);
          // Facts on right side, well spaced
          x = 1400;
          y = 200 + factIndex * 450;
        } else {
          // Others at bottom
          const otherSchemas = schemas.filter(
            (s) => !s.name.startsWith("DIM_") && !s.name.startsWith("FACT_")
          );
          const otherIndex = otherSchemas.indexOf(schema);

          x = 300 + otherIndex * 400;
          y = 1200;
        }
      }

      const node = {
        id: schema.name,
        type: "table",
        position: { x, y },
        data: {
          name: schema.name,
          description: schema.description,
          columns,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      };

      allNodes.push(node);
    });

    // Create all edges first
    const allEdges = [];
    schemas.forEach((schema) => {
      if (!schema.properties) return;

      const columns = (
        schema.column_order || Object.keys(schema.properties)
      ).map((name) => {
        const config = schema.properties[name];
        return {
          name,
          foreign_key: config.foreign_key || null,
        };
      });

      columns.forEach((column) => {
        if (column.foreign_key) {
          console.log(
            `Found FK: ${schema.name}.${column.name} -> ${column.foreign_key.table}.${column.foreign_key.column}`
          );

          // Check if target table exists in our schemas
          const targetExists = schemas.some(
            (s) => s.name === column.foreign_key.table
          );
          if (!targetExists) {
            console.warn(
              `Target table ${column.foreign_key.table} not found in schemas`
            );
            return;
          }

          const edge = {
            id: `${schema.name}-${column.name}-${column.foreign_key.table}`,
            source: schema.name,
            target: column.foreign_key.table,
            label: `${column.name} → ${column.foreign_key.column}`,
            type: "smoothstep",
            style: { stroke: "#6366f1", strokeWidth: 2 },
            labelStyle: { fontSize: 10, fill: "#6366f1" },
            labelBgPadding: [8, 4],
            labelBgBorderRadius: 4,
            labelBgStyle: { fill: "#f8fafc", fillOpacity: 0.8 },
            animated: true,
            markerEnd: {
              type: "arrowclosed",
              color: "#6366f1",
            },
          };

          allEdges.push(edge);
        }
      });
    });

    // Now filter nodes based on visibility settings
    const filteredNodes = allNodes.filter((node) => {
      const isDimension = node.data.name.startsWith("DIM_");
      const isFact = node.data.name.startsWith("FACT_");
      const isOther = !isDimension && !isFact;

      return (
        (isDimension && showDimensions) ||
        (isFact && showFacts) ||
        (isOther && showOthers)
      );
    });

    // Filter edges to only show those where both source and target are visible
    const visibleNodeIds = new Set(filteredNodes.map((node) => node.id));
    const filteredEdges = allEdges.filter(
      (edge) =>
        visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    );

    console.log(
      `Nodes: ${filteredNodes.length}, Edges: ${filteredEdges.length}`
    );
    console.log("All edges created:", allEdges);
    console.log("Filtered edges:", filteredEdges);

    return {
      filteredNodes,
      filteredEdges,
    };
  }, [schemas, showDimensions, showFacts, showOthers, layoutType]);

  // Update nodes and edges when filtered data changes
  useEffect(() => {
    setNodes(filteredNodes);
    setEdges(filteredEdges);
  }, [filteredNodes, filteredEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  useEffect(() => {
    fetchSchemas();
  }, []);

  // Copy positions to clipboard (inspired by SQL Habit - Ctrl+P)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === "p") {
        e.preventDefault();
        const positions = {};
        nodes.forEach((node) => {
          positions[node.id] = {
            x: Math.round(node.position.x),
            y: Math.round(node.position.y),
          };
        });
        navigator.clipboard.writeText(JSON.stringify(positions, null, 2));
        console.log("Node positions copied to clipboard!");
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [nodes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">
          Loading schemas...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 dark:text-red-400 p-4">
        <h3 className="font-medium mb-2">Error loading schemas</h3>
        <p className="text-sm mb-4">{error}</p>
        <Button onClick={fetchSchemas} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
      style={{ height: "calc(100vh - 64px)" }}
    >
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate("/dwh-schemas")}
            variant="outline"
            size="sm"
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span>DWH Schemas</span>
          </Button>
          <div>
            <h2 className="text-2xl font-bold dark:text-white">
              DWH Visualization
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Interactive schema diagram with {schemas.length} tables
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={layoutType}
            onChange={(e) => setLayoutType(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="hierarchical">Hierarchical</option>
            <option value="circular">Circular</option>
            <option value="force">Force Layout</option>
          </select>
          <Button onClick={fetchSchemas} variant="ghost" size="sm">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* React Flow Container */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{
            padding: 100,
            includeHiddenNodes: false,
            minZoom: 0.1,
            maxZoom: 1.5,
          }}
          className="bg-gray-50 dark:bg-gray-900"
          defaultEdgeOptions={{
            type: "smoothstep",
            style: { stroke: "#6366f1", strokeWidth: 2 },
          }}
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
        >
          <Background color="#aaa" gap={16} className="dark:opacity-20" />
          <Controls className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700" />
          <MiniMap
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
            nodeColor={(node) => {
              if (node.data.name.startsWith("DIM_")) return "#3b82f6";
              if (node.data.name.startsWith("FACT_")) return "#10b981";
              return "#6b7280";
            }}
          />

          {/* Legend Panel */}
          <Panel
            position="top-left"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg"
          >
            <div className="space-y-2">
              <h3 className="font-medium text-sm dark:text-white mb-2">
                Table Types
              </h3>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showDimensions}
                  onChange={(e) => setShowDimensions(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span className="text-xs dark:text-white">
                    Dimensions (
                    {schemas.filter((s) => s.name.startsWith("DIM_")).length})
                  </span>
                </div>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showFacts}
                  onChange={(e) => setShowFacts(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-xs dark:text-white">
                    Facts (
                    {schemas.filter((s) => s.name.startsWith("FACT_")).length})
                  </span>
                </div>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOthers}
                  onChange={(e) => setShowOthers(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gray-500 rounded"></div>
                  <span className="text-xs dark:text-white">
                    Others (
                    {
                      schemas.filter(
                        (s) =>
                          !s.name.startsWith("DIM_") &&
                          !s.name.startsWith("FACT_")
                      ).length
                    }
                    )
                  </span>
                </div>
              </label>
            </div>
          </Panel>

          {/* Stats Panel */}
          <Panel
            position="top-right"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg"
          >
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Visible Tables:
                </span>
                <span className="font-medium dark:text-white">
                  {filteredNodes.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Relations:
                </span>
                <span className="font-medium dark:text-white">
                  {filteredEdges.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Total Schemas:
                </span>
                <span className="font-medium dark:text-white">
                  {schemas.length}
                </span>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  💡 Ctrl+P to copy positions
                </div>
              </div>
              {filteredEdges.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    FK Relations:
                  </div>
                  {filteredEdges.slice(0, 3).map((edge) => (
                    <div key={edge.id} className="text-xs font-mono truncate">
                      {edge.source} → {edge.target}
                    </div>
                  ))}
                  {filteredEdges.length > 3 && (
                    <div className="text-xs text-gray-500">
                      +{filteredEdges.length - 3} more
                    </div>
                  )}
                </div>
              )}
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </div>
  );
};

export default DWHVisualization;
